import { useImperativeHandle, useRef, type Ref, type RefObject } from 'react';

import { useMergeRefs } from '../../../core/hooks/useMergeRefs.js';
import { useDataTableConfigElement } from '../../hooks/useDataTableConfig.js';
import type { DataTableColumnSettingsModalRef } from '../components/Toolbar/ColumnSettings/column-settings-types.js';
import { DATA_TABLE_DETAILS_ROW_IDENTIFIER } from '../constants.js';
import type {
  DataTableColumnSettingsConfig,
  DataTableRef,
} from '../public.api.js';
import type { DataTableRowData } from '../row-data-types.js';
import type { DataTableDownloadHeaderContext } from './Download/download-types.js';
import type {
  DataTableRowThreshold,
  DataTableThresholdProps,
} from './Thresholds/threshold-types.js';
import type { DataTableInstance } from './types/table.types.js';

/**
 * Extends the ref of the DataTable with additional configuration and control options.
 * @internal
 */
export function useDataTableConfigRef<TData extends DataTableRowData>(
  forwardedRef: Ref<DataTableRef>,
  columnSettingsModalRef: RefObject<DataTableColumnSettingsModalRef | null>,
  table: DataTableInstance<Exclude<TData, unknown>>,
  props: DataTableThresholdProps<TData>,
) {
  /*
   * Collect the refs and create an imperative handle in order to expose the
   * functions we need on the DataTable.
   */
  const containerRef = useRef<HTMLDivElement>(null);
  const rootRef = useMergeRefs<DataTableRef | HTMLDivElement>([
    forwardedRef,
    containerRef,
  ]);

  const configRowThresholds = useDataTableConfigElement('rowThresholds');

  useImperativeHandle(forwardedRef, () => ({
    element: containerRef.current,
    // @ts-expect-error: the `getConfig()` return type doesn't exactly satisfy `DataTableRef`'s `DataTableConfig` requirement due to internal table state type casting

    getConfig() {
      // TanStack Table v9.0.0-beta.52 initializes `columnVisibility` to `{}` instead of
      // leaving it undefined. The config is persisted by consumers, so an empty
      // object is kept out of the serialized output to keep it byte-compatible.
      const columnVisibility = table.atoms.columnVisibility?.get();
      const pagination = table.atoms.pagination?.get();

      return {
        pagination: {
          defaultPageIndex: pagination?.pageIndex,
          defaultPageSize: pagination?.pageSize,
        },
        columnVisibility:
          columnVisibility && Object.keys(columnVisibility).length > 0
            ? columnVisibility
            : undefined,
        fullWidth: table.options.layout?.fullWidth,
        fullHeight: table.options.layout?.fullHeight,
        lineWrap: table.atoms.columnLineWrap?.get(),
        resizable: table.options.enableColumnResizing,
        selectableRows: table.options.enableRowSelection,
        // NOTE: typecast necessary due to generic resolving to unknown
        rowThresholds: (props.rowThresholds ?? configRowThresholds) as
          | DataTableRowThreshold<TData>[]
          | undefined,
        sortable: table.options.enableSorting,
        variant: table.atoms.visualVariantConfig?.get(),
      };
    },
    downloadData(
      subset: 'all' | 'page' | 'selected',
      excludeColumns?: string[],
      formatDownloadHeader?: (
        context: DataTableDownloadHeaderContext,
      ) => string | undefined,
      fileName?: string,
    ) {
      switch (subset) {
        case 'all':
          table.downloadAllData(excludeColumns, formatDownloadHeader, fileName);
          break;
        case 'page':
          table.downloadPageData(
            excludeColumns,
            formatDownloadHeader,
            fileName,
          );
          break;
        case 'selected':
          table.downloadSelectedData(
            excludeColumns,
            formatDownloadHeader,
            fileName,
          );
          break;
      }
    },
    scrollToRow: table.scrollToRow,
    getDisplayedRowIds(): string[] {
      const result: string[] = [];
      for (const row of table.getRowModel().rows) {
        if (!row[DATA_TABLE_DETAILS_ROW_IDENTIFIER]) {
          result.push(row.id);
        }
      }
      return result;
    },
    openColumnSettings: (columnSettings?: DataTableColumnSettingsConfig) => {
      columnSettingsModalRef.current?.openColumnSettings(columnSettings);
    },
  }));

  return rootRef;
}
